import { ChainId, JSBI, Percent, Token, WETH } from 'toonswap-sdk'

export const ROUTER_ADDRESS = '0x7481313e8A71437B67d9Dd03ae38c5aB1531E0Ae'

// a list of tokens by chain
type ChainTokenList = {
  readonly [chainId in ChainId]: Token[]
}

export const USDT = new Token(ChainId.MAINNET, '0xdAC17F958D2ee523a2206206994597C13D831ec7', 6, 'USDT', 'Tether USD')
export const USDC = new Token(ChainId.MAINNET, '0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48', 6, 'USDC', 'USD Coin')
export const ETH = new Token(ChainId.MAINNET, '0xeAB3aC417c4d6dF6b143346a46fEe1B847B50296', 18, 'ETH', 'Ether')
export const TOON = new Token(
  ChainId.MAINNET,
  '0x12605027AF4A482BE2799dD8F38F095Aa093CCC4',
  18,
  'TOON',
  'ToonSwap Token',
)

export const SUPRA = new Token(ChainId.MAINNET, '0xDC7404100A092c898E93708EF73F3322A7acc213', 18, 'SUPRA', 'Supra')
export const WBTC = new Token(ChainId.MAINNET, '0x2260FAC5E5542a773Aa44fBCfeDf7C193bc2C599', 8, 'WBTC', 'Wrapped BTC')
export const PAW = new Token(ChainId.MAINNET, '0x5555dd68e459c7b29e774D702Fd121d5528E6c75', 18, 'PAW', 'SUPAW')

const WETH_ONLY: ChainTokenList = {
  [ChainId.MAINNET]: [WETH[ChainId.MAINNET]],
  [ChainId.BSCTESTNET]: [WETH[ChainId.BSCTESTNET]],
}

// used to construct intermediary pairs for trading
export const BASES_TO_CHECK_TRADES_AGAINST: ChainTokenList = {
  ...WETH_ONLY,
  [ChainId.MAINNET]: [TOON, SUPRA, USDC, USDT, WETH[ChainId.MAINNET], WBTC],
}

/**
 * Some tokens can only be swapped via certain pairs, so we override the list of bases that are considered for these
 * tokens.
 */
export const CUSTOM_BASES: { [chainId in ChainId]?: { [tokenAddress: string]: Token[] } } = {
  [ChainId.MAINNET]: {},
}

// used for display in the default list when adding liquidity
export const SUGGESTED_BASES: ChainTokenList = {
  ...WETH_ONLY,
  [ChainId.MAINNET]: [TOON, SUPRA, USDC, USDT, WBTC],
}

// used to construct the list of all pairs we consider by default in the frontend
export const BASES_TO_TRACK_LIQUIDITY_FOR: ChainTokenList = {
  ...WETH_ONLY,
  [ChainId.MAINNET]: [TOON, SUPRA, USDC, USDT, WETH[ChainId.MAINNET], WBTC],
}

export const PINNED_PAIRS: { readonly [chainId in ChainId]?: [Token, Token][] } = {
  [ChainId.MAINNET]: [
    [TOON, SUPRA],
    [SUPRA, USDC],
    [SUPRA, USDT],
    [SUPRA, WETH[ChainId.MAINNET]],
    [WETH[ChainId.MAINNET], USDC],
    [USDT, USDC],
    [WETH[ChainId.MAINNET], WBTC],
  ],
}

export const NetworkContextName = 'NETWORK'

// default allowed slippage, in bips
export const INITIAL_ALLOWED_SLIPPAGE = 80
// extra slippage (bps) for add-liquidity minimums to reduce INSUFFICIENT_*_AMOUNT reverts
export const ADD_LIQUIDITY_SLIPPAGE_BUFFER_BPS = 50
// 20 minutes, denominated in seconds
export const DEFAULT_DEADLINE_FROM_NOW = 60 * 20

// one basis point
export const ONE_BIPS = new Percent(JSBI.BigInt(1), JSBI.BigInt(10000))
export const BIPS_BASE = JSBI.BigInt(10000)
// used for warning states
export const ALLOWED_PRICE_IMPACT_LOW: Percent = new Percent(JSBI.BigInt(100), BIPS_BASE) // 1%
export const ALLOWED_PRICE_IMPACT_MEDIUM: Percent = new Percent(JSBI.BigInt(300), BIPS_BASE) // 3%
export const ALLOWED_PRICE_IMPACT_HIGH: Percent = new Percent(JSBI.BigInt(500), BIPS_BASE) // 5%
// if the price slippage exceeds this number, force the user to type 'confirm' to execute
export const PRICE_IMPACT_WITHOUT_FEE_CONFIRM_MIN: Percent = new Percent(JSBI.BigInt(1000), BIPS_BASE) // 10%
// for non expert mode disable swaps above this
export const BLOCKED_PRICE_IMPACT_NON_EXPERT: Percent = new Percent(JSBI.BigInt(1500), BIPS_BASE) // 15%

// used to ensure the user doesn't send so much ETH so they end up with <.002, enough gas for a swap
export const MIN_ETH: JSBI = JSBI.multiply(JSBI.BigInt(2), JSBI.exponentiate(JSBI.BigInt(10), JSBI.BigInt(15))) // .002 ETH

export const SWAP_MINING_ADDRESS = '0x9b8caa0d8f94A346e6Eca8e1E78687ABD38B0E65'
